import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Settings,
  Database,
  CreditCard,
  Brain,
  Mail,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
  Info,
  Zap,
  List,
  CheckCircle,
  XCircle,
  Trash2,
  Clock
} from 'lucide-react';
import { getBackendURL } from '../../utils/backendURL';

const SystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showSensitive, setShowSensitive] = useState({});
  
  // SMTP Test state
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  
  // Email Logs state
  const [showEmailLogs, setShowEmailLogs] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsStats, setEmailLogsStats] = useState({ total_sent: 0, total_failed: 0 });
  const [logsFilter, setLogsFilter] = useState('all');

  const BACKEND_URL = getBackendURL();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ops/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      if (error.response?.status === 403) {
        setError('Access denied. Only super admins can view system settings.');
      } else {
        setError('Failed to load system settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (key, currentValue) => {
    setEditingKey(key);
    setEditValue(currentValue || '');
    setSuccess(null);
    setError(null);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (key, category) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${BACKEND_URL}/api/ops/settings/${key}`,
        { value: editValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Setting ${key} updated successfully`);
      setEditingKey(null);
      setEditValue('');
      
      // Refresh settings
      await fetchSettings();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to update setting:', error);
      setError(error.response?.data?.detail || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSensitive = async (key) => {
    // If we're about to show the value and don't have it yet, fetch it
    if (!showSensitive[key]) {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await axios.get(`${BACKEND_URL}/api/ops/settings/${key}/reveal`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.value) {
          // Update the settings with the revealed value
          setSettings(prev => {
            const newSettings = { ...prev };
            // Find and update the setting in the correct category
            for (const category of Object.keys(newSettings)) {
              if (newSettings[category][key]) {
                newSettings[category][key] = {
                  ...newSettings[category][key],
                  revealedValue: response.data.value
                };
                break;
              }
            }
            return newSettings;
          });
        }
      } catch (error) {
        console.error('Failed to reveal setting:', error);
        setError('Failed to reveal setting value');
        return;
      }
    }
    setShowSensitive(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Test SMTP Connection
  const testSmtpConnection = async () => {
    try {
      setTestingSmtp(true);
      setSmtpTestResult(null);
      setError(null);

      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ops/test-smtp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Handle both success and error responses from the endpoint
      const data = response.data;
      setSmtpTestResult({
        success: data.status === 'success',
        message: data.message,
        settings: data.settings,
        checks_passed: data.checks_passed,
        error: data.error,
        errors: data.errors,
        troubleshooting: data.troubleshooting
      });
    } catch (error) {
      console.error('SMTP test failed:', error);
      const errorData = error.response?.data;
      setSmtpTestResult({
        success: false,
        message: errorData?.message || 'SMTP connection failed',
        error: errorData?.error || error.message,
        settings: errorData?.settings,
        troubleshooting: errorData?.troubleshooting || ['Check backend logs for details']
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  // Fetch Email Logs
  const fetchEmailLogs = async () => {
    try {
      setEmailLogsLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = logsFilter !== 'all' ? `?status_filter=${logsFilter}` : '';
      const response = await axios.get(
        `${BACKEND_URL}/api/ops/email-logs${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailLogs(response.data.logs);
      setEmailLogsStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
    } finally {
      setEmailLogsLoading(false);
    }
  };

  // Clear old logs
  const clearOldLogs = async () => {
    if (!window.confirm('Delete email logs older than 30 days?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${BACKEND_URL}/api/ops/email-logs?days_to_keep=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEmailLogs();
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  };

  useEffect(() => {
    if (showEmailLogs) {
      fetchEmailLogs();
    }
  }, [showEmailLogs, logsFilter]);

  const getCategoryIcon = (category) => {
    const icons = {
      database: Database,
      ai_services: Brain,
      payment: CreditCard,
      application: Globe,
      email: Mail
    };
    return icons[category] || Settings;
  };

  const getCategoryColor = (category) => {
    const colors = {
      database: 'bg-purple-500',
      ai_services: 'bg-blue-500',
      payment: 'bg-green-500',
      application: 'bg-orange-500',
      email: 'bg-pink-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="admin-card p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
              <div className="space-y-3">
                <div className="h-16 bg-gray-100 rounded"></div>
                <div className="h-16 bg-gray-100 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && error.includes('Access denied')) {
    return (
      <div className="admin-card p-6 border-red-200 bg-red-50">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
          <div>
            <h3 className="text-red-800 font-semibold">Access Denied</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="h-7 w-7 mr-2 text-indigo-600" />
            System Settings
          </h1>
          <p className="text-gray-600 mt-1">Manage environment variables and system configuration</p>
        </div>
        <button
          onClick={fetchSettings}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Warning Banner */}
      <div className="admin-card p-4 bg-yellow-50 border-yellow-200">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-yellow-800">Important Warning</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Changing these settings can affect system behavior. Some changes may require a restart.
              Read-only settings (like database connection) cannot be changed and require manual .env updates + restart.
            </p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="admin-card p-4 bg-green-50 border-green-200">
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-600 mr-2" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {error && !error.includes('Access denied') && (
        <div className="admin-card p-4 bg-red-50 border-red-200">
          <div className="flex items-center">
            <X className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Settings by Category */}
      {settings && Object.entries(settings).map(([category, categorySettings]) => {
        const Icon = getCategoryIcon(category);
        const colorClass = getCategoryColor(category);
        const isEmailCategory = category === 'email';

        return (
          <div key={category} className="admin-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${colorClass} mr-3`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 capitalize">
                  {category.replace('_', ' ')}
                </h2>
              </div>
              
              {/* Email Category Actions */}
              {isEmailCategory && (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={testSmtpConnection}
                    disabled={testingSmtp}
                    className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50"
                  >
                    <Zap className={`w-4 h-4 mr-2 ${testingSmtp ? 'animate-pulse' : ''}`} />
                    {testingSmtp ? 'Testing...' : 'Test SMTP'}
                  </button>
                  <button
                    onClick={() => setShowEmailLogs(!showEmailLogs)}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                      showEmailLogs 
                        ? 'bg-pink-100 text-pink-700 border border-pink-300' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <List className="w-4 h-4 mr-2" />
                    Email Logs
                  </button>
                </div>
              )}
            </div>

            {/* SMTP Test Result */}
            {isEmailCategory && smtpTestResult && (
              <div className={`mb-6 p-4 rounded-lg border ${
                smtpTestResult.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start">
                  {smtpTestResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`font-semibold ${smtpTestResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {smtpTestResult.message}
                    </p>
                    
                    {/* Success: Show checks passed */}
                    {smtpTestResult.success && smtpTestResult.checks_passed && (
                      <div className="mt-3 space-y-1">
                        {smtpTestResult.checks_passed.map((check, idx) => (
                          <p key={idx} className="text-sm text-green-700">{check}</p>
                        ))}
                      </div>
                    )}
                    
                    {/* Success: Show settings */}
                    {smtpTestResult.success && smtpTestResult.settings && (
                      <div className="mt-3 p-3 bg-green-100 rounded-lg">
                        <p className="text-sm font-medium text-green-800 mb-2">Connection Settings:</p>
                        <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                          <p>Host: <span className="font-mono">{smtpTestResult.settings.host}</span></p>
                          <p>Port: <span className="font-mono">{smtpTestResult.settings.port}</span></p>
                          <p>Username: <span className="font-mono">{smtpTestResult.settings.username}</span></p>
                          <p>From: <span className="font-mono">{smtpTestResult.settings.from_email}</span></p>
                          <p>SSL: {smtpTestResult.settings.ssl ? '✓ Enabled' : '✗ Disabled'}</p>
                          <p>Password: {smtpTestResult.settings.password_configured ? '✓ Configured' : '✗ Not Set'}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Error: Show detailed error */}
                    {!smtpTestResult.success && smtpTestResult.error && (
                      <div className="mt-3 p-3 bg-red-100 rounded-lg">
                        <p className="text-sm font-medium text-red-800 mb-1">Error Details:</p>
                        <p className="text-sm text-red-700 font-mono break-all">{smtpTestResult.error}</p>
                      </div>
                    )}
                    
                    {/* Error: Show validation errors */}
                    {!smtpTestResult.success && smtpTestResult.errors && smtpTestResult.errors.length > 0 && (
                      <div className="mt-3 p-3 bg-red-100 rounded-lg">
                        <p className="text-sm font-medium text-red-800 mb-2">Configuration Issues:</p>
                        <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                          {smtpTestResult.errors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Error: Show settings used */}
                    {!smtpTestResult.success && smtpTestResult.settings && (
                      <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                        <p className="text-sm font-medium text-gray-800 mb-2">Current Settings:</p>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                          <p>Host: <span className="font-mono">{smtpTestResult.settings.host || '(not set)'}</span></p>
                          <p>Port: <span className="font-mono">{smtpTestResult.settings.port || '(not set)'}</span></p>
                          <p>Username: <span className="font-mono">{smtpTestResult.settings.username || '(not set)'}</span></p>
                          <p>From: <span className="font-mono">{smtpTestResult.settings.from_email || '(not set)'}</span></p>
                          <p>SSL: {smtpTestResult.settings.ssl ? 'Enabled' : 'Disabled'}</p>
                          <p>Password: {smtpTestResult.settings.password_configured ? '✓ Set' : '✗ Not Set'}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Error: Show troubleshooting tips */}
                    {!smtpTestResult.success && smtpTestResult.troubleshooting && smtpTestResult.troubleshooting.length > 0 && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Troubleshooting Tips:
                        </p>
                        <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                          {smtpTestResult.troubleshooting.map((tip, idx) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Email Logs Panel */}
            {isEmailCategory && showEmailLogs && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <h3 className="font-semibold text-gray-900">Email Logs</h3>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {emailLogsStats.total_sent} sent
                      </span>
                      <span className="flex items-center text-red-600">
                        <XCircle className="w-4 h-4 mr-1" />
                        {emailLogsStats.total_failed} failed
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={logsFilter}
                      onChange={(e) => setLogsFilter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    >
                      <option value="all">All</option>
                      <option value="success">Success</option>
                      <option value="failed">Failed</option>
                    </select>
                    <button
                      onClick={fetchEmailLogs}
                      disabled={emailLogsLoading}
                      className="p-2 text-gray-600 hover:text-gray-800"
                    >
                      <RefreshCw className={`w-4 h-4 ${emailLogsLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={clearOldLogs}
                      className="p-2 text-red-600 hover:text-red-800"
                      title="Clear logs older than 30 days"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {emailLogsLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading logs...</div>
                ) : emailLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No email logs found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Recipient</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Subject</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Time</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log) => (
                          <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-100">
                            <td className="py-2 px-3">
                              {log.status === 'success' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Sent
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Failed
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 font-mono text-xs">{log.recipient}</td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                                {log.email_type}
                              </span>
                            </td>
                            <td className="py-2 px-3 max-w-xs truncate" title={log.subject}>
                              {log.subject}
                            </td>
                            <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                              <span className="flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {formatDate(log.sent_at)}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-red-600 max-w-xs truncate" title={log.error_message}>
                              {log.error_message || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(categorySettings).map(([key, data]) => {
                const schema = data.schema;
                const isEditing = editingKey === key;
                const isSensitive = schema.sensitive;
                const isReadonly = schema.readonly;

                return (
                  <div
                    key={key}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      isReadonly
                        ? 'bg-gray-50 border-gray-200'
                        : isEditing
                        ? 'bg-indigo-50 border-indigo-300'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{schema.label}</h3>
                          {isSensitive && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center">
                              <Lock className="w-3 h-3 mr-1" />
                              Sensitive
                            </span>
                          )}
                          {isReadonly && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                              Read-only
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {data.source}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{schema.description}</p>

                        {isEditing ? (
                          <div className="space-y-3">
                            <input
                              type={isSensitive && !showSensitive[key] ? 'password' : 'text'}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder={`Enter ${schema.label}`}
                            />
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleSave(key, category)}
                                disabled={saving}
                                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={handleCancel}
                                className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3">
                            <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono text-gray-800 break-all">
                              {isSensitive && showSensitive[key]
                                ? (data.revealedValue || data.value || '(not set)')
                                : (data.value || '(not set)')}
                            </code>
                            {isSensitive && (
                              <button
                                onClick={() => toggleShowSensitive(key)}
                                className="p-2 text-gray-500 hover:text-gray-700"
                                title={showSensitive[key] ? 'Hide' : 'Show'}
                              >
                                {showSensitive[key] ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {!isEditing && !isReadonly && (
                        <button
                          onClick={() => handleEdit(key, data.value)}
                          className="ml-4 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                        >
                          Edit
                        </button>
                      )}
                    </div>

                    {isReadonly && (
                      <div className="mt-3 flex items-start p-3 bg-gray-100 rounded-lg">
                        <Info className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-gray-600">
                          This setting cannot be changed at runtime. To modify, update the .env file manually and restart the backend service.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SystemSettings;
